import {hpcAttrs} from '@github-ui/hpc'
import {Link} from '@github-ui/react-router'
import {testIdProps} from '@github-ui/test-id-props'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {Button, Heading, Link as PrimerLink, LinkButton} from '@primer/react'

import styles from './EmptyState.module.css'

const DOCS_URL = 'https://docs.github.com/actions/using-github-hosted-runners/about-github-hosted-runners'

export interface EmptyStateProps {
  selectedTab: 'github-hosted' | 'self-hosted'
  setUpRunnersLink?: string
}

export function EmptyState({selectedTab, setUpRunnersLink, ...props}: EmptyStateProps) {
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  const setUpRunnersCta = setUpRunnersLink && (
    <Button
      as={Link}
      to={setUpRunnersLink}
      variant="primary"
      className={styles.newRunnerButton}
      onClick={() => {
        sendClickAnalyticsEvent({
          category: 'repository_runners',
          action: 'click_new_runner_cta',
          label: `ref_cta:new_runner;ref_loc:empty_state;path:${setUpRunnersLink};selected_tab:${selectedTab}`,
        })
      }}
      {...testIdProps('empty-state-new-runner-cta')}
    >
      New runner
    </Button>
  )

  let docsCta = null
  // Don't show docs CTA on self-hosted tab
  if (selectedTab === 'github-hosted') {
    const sendAnalyticsDocs = () => {
      sendClickAnalyticsEvent({
        category: 'repository_runners',
        action: 'click_view_runner_docs',
        label: `ref_cta:learn_more_about_using_runners;ref_loc:empty_state;selected_tab:${selectedTab}`,
      })
    }

    if (setUpRunnersLink) {
      docsCta = (
        <PrimerLink href={DOCS_URL} className={styles.learnMoreLink} onClick={sendAnalyticsDocs}>
          Learn more about using runners
        </PrimerLink>
      )
    } else {
      docsCta = (
        <LinkButton
          href={DOCS_URL}
          variant="primary"
          className={styles.learnMoreButton}
          onClick={sendAnalyticsDocs}
          {...testIdProps('empty-state-learn-more-cta')}
        >
          Learn more about using runners
        </LinkButton>
      )
    }
  }

  const descriptionText =
    selectedTab === 'github-hosted'
      ? 'Runners are the machines that execute GitHub Actions workflows.'
      : 'Self-hosted runners are virtual machines for GitHub Actions workflows that you manage and maintain outside of GitHub.'

  return (
    <div {...hpcAttrs()} className={styles.container} {...testIdProps('empty-state')} {...props}>
      <Heading as="h2" className={styles.heading}>
        {`You don't have any ${selectedTab === 'self-hosted' ? 'self-hosted ' : ''}runners for this repository`}
      </Heading>
      <span>{descriptionText}</span>
      {setUpRunnersCta}
      {docsCta}
    </div>
  )
}
